import {
    Chip,
    ChipDateRangePicker,
    ChipMenu,
    Dropdown,
    Input,
    MenuItemProps,
    useListFilterState,
    useRuntimeEnv,
    useTranslation,
    useTranslations,
} from "@harea/component";
import { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { dayOffStatus } from "../../../components/StatusSelect";
import _ from "lodash";
import { useDepartmentList } from "views/dayoff/hooks/useDepartmentList";
import { useTimeOffTypeList } from "views/create/hooks/useTimeOffTypeList";

export const DayOffListQuickFilter = (props: Props) => {
    const [STATUS, DATERANGE, DEPARTMENT, EMPLOYEE, TYPE] = useTranslations(i18n);
    const t = useTranslation();

    const { filter, setFilter } = useListFilterState(props.listId);
    const departments = useDepartmentList();
    const timeOffTypes = useTimeOffTypeList('LEAVE');

    const [nameAnchorEl, setNameAnchorEl] = useState<HTMLDivElement>();

    const dayOffStatusMenuItems = useMemo(
        () => [
            { label: STATUS, value: "default" },
            ...dayOffStatus.map((status) => ({
                ...status,
                label: t(status.label),
            })),
        ],
        [dayOffStatus, STATUS, t]
    );

    const typeMenuItems = useMemo(
        () => [
            { label: TYPE, value: "default", id: undefined },
            ...timeOffTypes,
        ],
        [timeOffTypes, TYPE]
    );

    const departmentStatusMenuItems = props.departmentId
        ? departments.filter((dept) => dept.value === props.departmentId)
        : departments

    const activeDepartmentMenu = useMemo(() => {
        if (props.departmentId) {
            return departmentStatusMenuItems.find((dept) => dept.value === props.departmentId);
        }
        const departmentId = filter.data?.employee?.department?.id;
        if (departmentId) {
            return departmentStatusMenuItems.find((dept) => dept.value === departmentId);
        }
        return departmentStatusMenuItems.find((dept) => dept.value === -1);
    }, [filter.data?.employee?.department?.id, departmentStatusMenuItems, props.departmentId]);

    const activeDayOffStatusMenu = useMemo(() => {
        if (filter.data?.status === "approved") {
            return dayOffStatusMenuItems.find((status) => status.value === "approved");
        }

        if (filter.data?.status === "validated") {
            return dayOffStatusMenuItems.find((status) => status.value === "validated");
        }

        if (filter.data?.status === "refused") {
            return dayOffStatusMenuItems.find((status) => status.value === "refused");
        }

        if (filter.data?.status === "new") {
            return dayOffStatusMenuItems.find((status) => status.value === "new");
        }

        if (filter.data?.status === "expired") {
            return dayOffStatusMenuItems.find((status) => status.value === "expired");
        }

        return dayOffStatusMenuItems.find((status) => status.value === "default");
    }, [filter.data, dayOffStatusMenuItems]);

    const activeTypeMenu = useMemo(() => {
        const typeId = filter.data?.type?.id;
        if (typeId) {
            return typeMenuItems.find((item: any) => item.id === typeId);
        }
        return typeMenuItems.find((item) => item.value === 'default');
    }, [filter.data?.type?.id, typeMenuItems]);

    const dateRange = useMemo((): [string, string] | undefined => {
        if (filter?.data?.dateBegin_supOrEquals) {
            return [
                filter?.data?.dateBegin_supOrEquals,
                filter?.data?.dateEnd_infOrEquals,
            ];
        }
        return undefined;
    }, [filter]);

    const handleDateRangeChange = useCallback(
        (value: [string, string]) => {
            setFilter((v: any) => ({
                ...v,
                data: {
                    ...v.data,
                    dateBegin_supOrEquals: value?.[0],
                    dateEnd_infOrEquals: value?.[1]
                },
            }));
        },
        [setFilter]
    );

    const handleDayOffStatusMenuClick = useCallback((menu: MenuItemProps) => {
        setFilter((v: any) => ({
            ...v,
            data: {
                ...v.data,
                dateBegin_supOrEquals: undefined,
                dateEnd_infOrEquals: undefined,
                status: menu.value === 'default' || menu.value === 'expired' ? undefined : menu.value,
                dateEnd_inf: menu.value === 'expired' ? moment().format("YYYY-MM-DD") : undefined,
            },
        }));
    }, [setFilter]);

    const handleDepartmentMenuClick = useCallback((menu: MenuItemProps) => {
        setFilter((v: any) => ({
            ...v,
            data: {
                ...v.data,
                employee: {
                    department: { id: menu.value }
                }
            },
        }));
    }, [setFilter]);

    const handleTypeMenuClick = useCallback((menu: MenuItemProps) => {
        if (menu.value === 'default') {
            setFilter((v: any) => ({
                ...v,
                data: {
                    ...v.data,
                    type: undefined,
                },
            }));
        } else {
            setFilter((v: any) => ({
                ...v,
                data: {
                    ...v.data,
                    type: {
                        id: menu.id,
                    },
                },
            }));
        }
    }, [setFilter]);

    const handleNameSearchChange = useCallback((value: string) => {
        setFilter((v: any) => ({
            ...v,
            data: {
                ...v.data,
                employee: {
                    ...v.data?.employee,
                    fullName_like: value || undefined,
                    department: v.data?.employee?.department,
                }
            },
        }));
    }, [setFilter]);

    const searchName = filter.data?.employee?.fullName_like || '';

    return (
        <div className="flex items-center justify-end gap-2">
            {!props.isMyTimeoffs && (
                <>
                    <Chip
                        className="bg-secondary"
                        label={searchName || EMPLOYEE}
                        endIcon="fa-chevron-down text-xs bg-transparent"
                        onClick={(e) => setNameAnchorEl(e.currentTarget)}
                    />
                    <Dropdown
                        anchorEl={nameAnchorEl}
                        arrow
                        placement="bottom-end"
                        onClose={() => setNameAnchorEl(undefined)}
                    >
                        <div className="p-3 min-w-[200px]">
                            <Input
                                name="employeeName"
                                value={searchName}
                                onChange={handleNameSearchChange}
                                placeholder={EMPLOYEE}
                            />
                        </div>
                    </Dropdown>
                </>
            )}

            <ChipDateRangePicker
                defaultLabel={DATERANGE}
                value={dateRange}
                onChange={handleDateRangeChange}
            />

            <ChipMenu
                value={activeDayOffStatusMenu?.value}
                items={dayOffStatusMenuItems}
                onClick={handleDayOffStatusMenuClick}
            />

            {!props.isMyTimeoffs && (
                <ChipMenu
                    value={activeTypeMenu?.value}
                    items={typeMenuItems}
                    onClick={handleTypeMenuClick}
                />
            )}

            {!props.isMyTimeoffs && (
                <ChipMenu
                    value={activeDepartmentMenu?.value}
                    items={departmentStatusMenuItems}
                    onClick={handleDepartmentMenuClick}
                />
            )}
        </div>
    );
};

const i18n = ["std_status", "std_daterange", "std_department", "std_employee", "std_type"];

type Props = {
    listId: string;
    departmentId?: number;
    isMyTimeoffs?: boolean;
};
